import React from 'react';
import { X, Plus, Minus, Trash2, ShoppingBag } from 'lucide-react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { ScrollArea } from './ui/scroll-area';
import { useTranslation } from '../hooks/useTranslation';

const CartSheet = ({ isOpen, onClose, cart, onUpdateQuantity, onRemoveItem, onCheckout, deliveryInfo }) => {
  const { t } = useTranslation();
  const subtotal = cart.reduce((total, item) => total + item.price * item.quantity, 0);
  
  // Calculate delivery fee based on subtotal
  let deliveryFee = 0;
  if (subtotal >= deliveryInfo.freeDeliveryOver) {
    deliveryFee = 0; // Free delivery for orders €35+
  } else if (subtotal >= deliveryInfo.minimumOrder) {
    deliveryFee = deliveryInfo.deliveryFee; // €2.50 for orders €20-35
  }
  
  const total = subtotal + deliveryFee;

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-lg bg-white">
        <SheetHeader>
          <SheetTitle className="text-2xl font-bold text-black flex items-center">
            <ShoppingBag className="w-6 h-6 mr-2" />
            {t('yourCart')}
          </SheetTitle>
        </SheetHeader>

        {cart.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-[calc(100vh-200px)] text-center px-4">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <ShoppingBag className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-xl font-bold text-black mb-2">{t('cartEmpty')}</h3>
            <p className="text-gray-600 mb-6">{t('cartEmptyDesc')}</p>
            <Button onClick={onClose} className="bg-black text-white hover:bg-gray-800">
              {t('browseMenu')}
            </Button>
          </div>
        ) : (
          <div className="flex flex-col h-[calc(100vh-100px)] mt-6">
            <ScrollArea className="flex-1 pr-4">
              <div className="space-y-4">
                {cart.map((item) => (
                  <div
                    key={item.id}
                    className="flex gap-4 p-4 bg-gray-50 rounded-xl border-2 border-gray-200 hover:border-black transition-all duration-300"
                  >
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-black mb-1 truncate">{item.name}</h3>
                      <p className="text-sm text-gray-600 mb-2">€{item.price.toFixed(2)} each</p>
                      <div className="flex items-center space-x-2">
                        <Button
                          size="icon"
                          variant="outline"
                          onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
                          className="h-8 w-8 rounded-lg border-2 border-gray-300 hover:border-black"
                        >
                          <Minus className="w-4 h-4" />
                        </Button>
                        <span className="w-8 text-center font-semibold text-black">{item.quantity}</span>
                        <Button
                          size="icon"
                          variant="outline"
                          onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                          className="h-8 w-8 rounded-lg border-2 border-gray-300 hover:border-black"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => onRemoveItem(item.id)}
                          className="h-8 w-8 ml-auto text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="font-bold text-black text-right">
                      €{(item.price * item.quantity).toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>

            <div className="border-t-2 border-gray-200 pt-6 mt-6 space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between text-gray-600">
                  <span>{t('subtotal')}</span>
                  <span className="font-semibold">€{subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>{t('deliveryFee')}</span>
                  <span className="font-semibold">
                    {subtotal >= deliveryInfo.freeDeliveryOver ? (
                      <span className="text-green-600">{t('free')}</span>
                    ) : subtotal >= deliveryInfo.minimumOrder ? (
                      `€${deliveryFee.toFixed(2)}`
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </span>
                </div>
                
                {subtotal < deliveryInfo.freeDeliveryOver && subtotal >= deliveryInfo.minimumOrder && (
                  <p className="text-xs text-gray-500">
                    {t('addMore')} €{(deliveryInfo.freeDeliveryOver - subtotal).toFixed(2)} for free delivery
                  </p>
                )}
                {subtotal < deliveryInfo.minimumOrder && (
                  <p className="text-xs text-red-500">
                    Minimum order €{deliveryInfo.minimumOrder}
                  </p>
                )}
                {subtotal >= deliveryInfo.minimumOrder && deliveryFee > 0 && (
                  <p className="text-xs text-blue-600">
                    €2.50 delivery for orders €20-35
                  </p>
                )}
              </div>
              
              <div className="flex justify-between text-xl font-bold text-black pt-2 border-t-2 border-gray-300">
                <span>{t('total')}</span>
                <span>€{total.toFixed(2)}</span>
              </div>
              
              {/* Minimum order info message */}
              <div className="p-3 bg-gray-100 border border-gray-300 rounded-lg">
                <p className="text-gray-700 text-sm text-center">
                  {t('minOrderDelivery')}
                </p>
              </div>

              <Button
                onClick={onCheckout}
                className="w-full bg-black text-white hover:bg-gray-800 font-semibold py-6 text-lg transition-all duration-300"
              >
                {t('proceedToCheckout')}
              </Button>
              <p className="text-xs text-center text-green-600 font-medium">
                💡 {t('saveOnPickup')}
              </p>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartSheet;